/**
 * 직원 ID 생성 및 정규화 유틸리티
 * 일관된 ID 체계를 유지하기 위한 헬퍼 함수들
 */

import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase.js';
import { AppError, ErrorCodes } from './errorHandler.ts';
import { logger } from './logger.ts';

/**
 * 다음 사용 가능한 직원 ID 생성
 * @returns 다음 사용 가능한 숫자 ID
 */
export const getNextEmployeeId = async (): Promise<number> => {
  try {
    const employeesRef = collection(db, 'employees');
    const q = query(employeesRef, orderBy('id', 'desc'), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return 1; // 첫 번째 직원
    }

    const lastEmployee = snapshot.docs[0].data();
    const lastId = typeof lastEmployee.id === 'number'
      ? lastEmployee.id
      : parseInt(String(lastEmployee.id), 10) || 0;

    return lastId + 1;
  } catch (error) {
    logger.error('Error getting next employee ID:', error instanceof Error ? error : undefined);
    // 타임스탬프 기반 ID는 순차 스킴을 파괴하고(이후 max가 ~17억으로 팽창) 같은 초 동시
    // 호출 시 충돌하므로 폴백으로 쓰지 않는다. 호출부가 재시도/처리하도록 에러를 전파한다.
    throw error instanceof Error
      ? error
      : new AppError('다음 직원 ID를 생성하지 못했습니다.', ErrorCodes.UNKNOWN_ERROR);
  }
};

/**
 * 직원 ID 정규화 (문자열 → 숫자)
 * @param id - 원본 ID (숫자 또는 문자열)
 * @returns 정규화된 숫자 ID
 * @throws 유효하지 않은 ID인 경우 에러
 */
export const normalizeEmployeeId = (id: number | string | undefined | null): number => {
  // undefined나 null 처리
  if (id === undefined || id === null) {
    throw new AppError(
      'Employee ID is required',
      ErrorCodes.VALIDATION_ERROR,
      { receivedValue: id }
    );
  }

  // 이미 숫자인 경우
  if (typeof id === 'number') {
    if (id <= 0) {
      throw new AppError(
        `Invalid employee ID: ${id} (must be positive)`,
        ErrorCodes.VALIDATION_ERROR,
        { receivedValue: id }
      );
    }
    return id;
  }

  // 문자열인 경우
  if (typeof id === 'string') {
    // 빈 문자열 체크
    if (id.trim() === '') {
      throw new AppError(
        'Employee ID cannot be empty',
        ErrorCodes.VALIDATION_ERROR,
        { receivedValue: id }
      );
    }

    // 문자열을 숫자로 변환 시도
    const parsed = parseInt(id.trim(), 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }

    throw new AppError(
      `Invalid employee ID: "${id}" (cannot convert to number)`,
      ErrorCodes.VALIDATION_ERROR,
      { receivedValue: id }
    );
  }

  throw new AppError(
    `Invalid employee ID type: ${typeof id}`,
    ErrorCodes.VALIDATION_ERROR,
    { receivedType: typeof id, receivedValue: id }
  );
};

/**
 * 직원 이름으로부터 ID 매핑 조회
 * 레거시 데이터 마이그레이션 시 사용
 * @param employeeName - 직원 이름
 * @returns 매핑된 숫자 ID 또는 null
 */
export const getEmployeeIdByName = async (employeeName: string): Promise<number | null> => {
  try {
    const employeesRef = collection(db, 'employees');
    const snapshot = await getDocs(employeesRef);

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.name === employeeName) {
        try {
          return normalizeEmployeeId(data.id);
        } catch (error) {
          logger.warn(`Cannot normalize ID for employee ${employeeName}`, { context: { error } });
          return null;
        }
      }
    }

    return null;
  } catch (error) {
    logger.error('Error getting employee ID by name:', error instanceof Error ? error : undefined);
    return null;
  }
};

/**
 * 문서 ID에서 직원 ID 추출
 * 예: "3_2025-01-15" → 3
 * @param documentId - Firestore 문서 ID
 * @returns 추출된 직원 ID 또는 null
 */
export const extractEmployeeIdFromDocumentId = (documentId: string): number | null => {
  try {
    const parts = documentId.split('_');
    if (parts.length === 0) return null;

    const idPart = parts[0];
    const parsed = parseInt(idPart, 10);

    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }

    return null;
  } catch (error) {
    logger.error('Error extracting employee ID from document ID:', error instanceof Error ? error : undefined);
    return null;
  }
};

/**
 * ID 타입 확인 유틸리티
 * @param id - 확인할 ID
 * @returns ID 타입 정보
 */
export const identifyIdType = (id: any): {
  isNumeric: boolean;
  isStringNumeric: boolean;
  isStringName: boolean;
  normalizedValue: number | null;
} => {
  const result = {
    isNumeric: false,
    isStringNumeric: false,
    isStringName: false,
    normalizedValue: null as number | null
  };

  if (typeof id === 'number') {
    result.isNumeric = true;
    result.normalizedValue = id;
  } else if (typeof id === 'string') {
    const parsed = parseInt(id, 10);
    if (!isNaN(parsed)) {
      result.isStringNumeric = true;
      result.normalizedValue = parsed;
    } else {
      result.isStringName = true;
    }
  }

  return result;
};

/**
 * 안전한 ID 정규화 (에러 발생 시 원본 반환)
 * @param id - 원본 ID
 * @returns 정규화된 ID 또는 원본
 */
export const safeNormalizeEmployeeId = (id: number | string | undefined | null): number | string => {
  try {
    return normalizeEmployeeId(id);
  } catch (error) {
    logger.warn('ID normalization failed, returning original', { context: { error, id } });
    return id || '';
  }
};